/**
 * GLOBAL-FILTER-ENGINE-000001 — local persistence (session + prefs).
 * No backend. Remembers last context, theme, workspace, pinned filters, saved views.
 */
import { DEFAULT_CONTEXT } from './defaults';
import type { SavedFilterView, UniversalBusinessContext } from './types';

const CTX_KEY = 'gfe-universal-context-v1';
const VIEWS_KEY = 'gfe-saved-views-v1';
const RECENT_FILTERS_KEY = 'gfe-recent-filter-keys-v1';
const PINNED_FILTER_KEYS = 'gfe-pinned-filter-keys-v1';
const LAST_THEME_KEY = 'gfe-last-theme';
const LAST_WORKSPACE_KEY = 'gfe-last-workspace';

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function loadPersistedContext(): UniversalBusinessContext {
  try {
    const raw = localStorage.getItem(CTX_KEY);
    if (!raw) return { ...DEFAULT_CONTEXT };
    const parsed = JSON.parse(raw) as Partial<UniversalBusinessContext>;
    return { ...DEFAULT_CONTEXT, ...parsed };
  } catch {
    return { ...DEFAULT_CONTEXT };
  }
}

export function savePersistedContext(ctx: UniversalBusinessContext): void {
  try {
    localStorage.setItem(CTX_KEY, JSON.stringify(ctx));
    if (ctx.lastWorkspacePath) {
      localStorage.setItem(LAST_WORKSPACE_KEY, ctx.lastWorkspacePath);
    }
  } catch {
    /* ignore */
  }
}

export function loadLastWorkspacePath(): string | null {
  try {
    return localStorage.getItem(LAST_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

export function saveLastTheme(theme: string): void {
  try {
    localStorage.setItem(LAST_THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export function loadLastTheme(): string | null {
  try {
    return localStorage.getItem(LAST_THEME_KEY);
  } catch {
    return null;
  }
}

export function loadSavedViews(): SavedFilterView[] {
  return safeParse<SavedFilterView[]>(localStorage.getItem(VIEWS_KEY), []);
}

export function saveSavedViews(views: SavedFilterView[]): void {
  try {
    localStorage.setItem(VIEWS_KEY, JSON.stringify(views.slice(0, 40)));
  } catch {
    /* ignore */
  }
}

export function upsertSavedView(view: SavedFilterView): SavedFilterView[] {
  const all = loadSavedViews().filter((v) => v.id !== view.id);
  all.unshift(view);
  saveSavedViews(all);
  return all;
}

export function removeSavedView(id: string): SavedFilterView[] {
  const all = loadSavedViews().filter((v) => v.id !== id);
  saveSavedViews(all);
  return all;
}

export function loadPinnedFilterKeys(): string[] {
  return safeParse<string[]>(localStorage.getItem(PINNED_FILTER_KEYS), [
    'sellerLabel',
    'marketplace',
    'dateRange',
    'comparePeriod',
  ]);
}

export function savePinnedFilterKeys(keys: string[]): void {
  try {
    localStorage.setItem(PINNED_FILTER_KEYS, JSON.stringify(keys.slice(0, 24)));
  } catch {
    /* ignore */
  }
}

export function loadRecentFilterKeys(): string[] {
  return safeParse<string[]>(localStorage.getItem(RECENT_FILTERS_KEY), []);
}

export function pushRecentFilterKey(key: string): string[] {
  const next = [key, ...loadRecentFilterKeys().filter((k) => k !== key)].slice(0, 16);
  try {
    localStorage.setItem(RECENT_FILTERS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}
